"use client";

import { showOnlineExclusiveFeatureFlag } from "@utils/default";
import { useTranslations } from "next-intl";
import React, { FC, Fragment, ReactElement, useContext, useEffect, useMemo } from "react";
import { Control, UseFormSetValue, UseFormWatch } from "react-hook-form";

import Checkbox from "@/components/ui/Form/Filters/components/Checkbox/Checkbox";
import Icon from "@/components/ui/Form/Filters/components/Icon/Icon";
import Numbers from "@/components/ui/Form/Filters/components/Numbers/Numbers";
import Radio from "@/components/ui/Form/Filters/components/Radio/Radio";
import Range from "@/components/ui/Form/Filters/components/Range/Range";
import Year from "@/components/ui/Form/Filters/components/Year/Year";
import { SearchContext } from "@/context/SearchContext";
import { UserContext } from "@/context/UserContext";
import { CommercetoolsProject, PRODUCT_TYPE } from "@/types";

import Toggle from "../Toggle/Toggle";

const getComponentByKey = (
  filterKey: string,
  name: string,
  control: Control<any>,
  setValue: UseFormSetValue<any>,
  watch: UseFormWatch<any>,
  label: string,
  values?: (string | number)[] | undefined,
  displayInSearchBar?: boolean,
  availableValues?: (string | number)[] | undefined,
): ReactElement => {
  switch (filterKey) {
    case PRODUCT_TYPE.PROPERTY_TYPE:
    case "lifestyle":
    case "brands":
      return (
        <Icon
          displayInSearchBar={displayInSearchBar}
          values={values}
          control={control}
          id={`filter-${name}-${filterKey}`}
          name={filterKey}
          filter={filterKey}
          availableValues={availableValues}
        />
      );
      break;
    case "status":
    case "isFurnished":
      return (
        <Radio
          availableValues={availableValues}
          values={values}
          control={control}
          id={`filter-${name}-${filterKey}`}
          name={filterKey}
          filter={filterKey}
        />
      );
    case "onlineExclusive":
      if (!showOnlineExclusiveFeatureFlag) return <Fragment />;
      return (
        <Toggle
          // disabled={searchParams.get("type") === PRODUCT_TYPE.PROJECT}
          label={""}
          name={filterKey}
          control={control}
          value={watch("onlineExclusive")?.toString()}
          values={[true, false]}
          onChange={(e) => setValue("onlineExclusive", e.currentTarget.checked)}
        />
      );
    case "vTour":
      return (
        <Toggle
          // disabled={searchParams.get("type") === PRODUCT_TYPE.PROJECT}
          label={""}
          name={filterKey}
          control={control}
          value={watch("vTour")?.toString()}
          values={[true, false]}
          onChange={(e) => setValue("vTour", e.currentTarget.checked)}
        />
      );
    case "webIE":
      return (
        <Toggle
          // disabled={searchParams.get("type") === PRODUCT_TYPE.PROJECT}
          label={""}
          name={filterKey}
          control={control}
          value={watch("webIE")?.toString()}
          values={[true, false]}
          onChange={(e) => setValue("webIE", e.currentTarget.checked)}
        />
      );
    case "deliveryDate":
      return (
        <Year
          availableValues={availableValues}
          values={values}
          control={control}
          id={`filter-${name}-${filterKey}`}
          name={filterKey}
          filter={filterKey}
        />
      );
      break;
    case "price":
    case "area":
      return (
        <Range
          availableValues={availableValues as number[]}
          values={values as number[]}
          control={control}
          id={`filter-${name}-${filterKey}`}
          name={filterKey}
          label={label}
          filter={filterKey}
        />
      );
    case "bedrooms":
    case "bathrooms":
      return (
        <Numbers
          displayInSearchBar={displayInSearchBar}
          values={values}
          control={control}
          id={`filter-${name}-${filterKey}`}
          name={filterKey}
          filter={filterKey}
          availableValues={availableValues}
        />
      );
    default:
      return (
        <Checkbox
          availableValues={availableValues}
          values={values}
          control={control}
          id={`filter-${name}-${filterKey}`}
          name={filterKey}
          filter={filterKey}
        />
      );
  }
};
interface Props {
  name: string;
  filterKey: string;
  control: Control<any>;
  setValue: UseFormSetValue<any>;
  watch: UseFormWatch<any>;
  values: (string | number)[] | undefined;
  displayInSearchBar?: boolean;
}

const FilterComponent: FC<Props> = ({ name, filterKey, control, setValue, watch, values, displayInSearchBar = false }) => {
  const t = useTranslations();
  const { filteredTotalResults } = useContext(SearchContext);
  const { currency, measurement } = useContext(UserContext);
  const availableValues: (string | number)[] = useMemo(() => {
    let filteredValues = filteredTotalResults.map((item: CommercetoolsProject) => {
      switch (filterKey) {
        case "deliveryDate":
          //return !!item?.[filterKey as keyof CommercetoolsProject] ? `${(item[filterKey as keyof CommercetoolsProject] as Date)?.getFullYear()}` : "";
          const rawDate = item?.[filterKey as keyof CommercetoolsProject];
          if (!rawDate) return "";
          // Ensure rawDate is a valid date before using new Date()
          if (typeof rawDate === "string" || typeof rawDate === "number") {
            const parsedDate = new Date(rawDate);
            return !isNaN(parsedDate.getTime()) ? parsedDate.getFullYear().toString() : "";
          }
          // If rawDate is already a Date object, use it directly
          if (rawDate instanceof Date) {
            return rawDate.getFullYear().toString();
          }
          return "";
        case "area":
          return [
            item?.areaMinConversion?.[measurement] && item?.areaMinConversion?.[measurement] > 0 ? item?.areaMinConversion?.[measurement] : 0,
            item?.areaMaxConversion?.[measurement] && item?.areaMaxConversion?.[measurement] > 0 ? item?.areaMaxConversion?.[measurement] : 0,
          ];
        case "price":
          return [
            item?.priceMinCurrencies?.[currency] && item.priceMinCurrencies[currency] > 0 ? item.priceMinCurrencies[currency] : 0,
            item?.priceMaxCurrencies?.[currency] && item.priceMaxCurrencies[currency] > 0 ? item.priceMaxCurrencies[currency] : 0,
          ];
        case "others":
          if (item.goldenVisa) {
            return "goldenVisa";
          }
          return "goldenVisa";
        default:
          return item[filterKey as keyof CommercetoolsProject];
      }
    });
    if (["price", "area"].includes(filterKey)) {
      filteredValues = filteredValues.flat().sort((a, b) => (a as number) - (b as number));
      filteredValues = [filteredValues[0], filteredValues[filteredValues.length - 1]];
    }
    return Array.from(new Set(filteredValues.flat())) as (string | number)[];
  }, [filteredTotalResults, filterKey, currency]);
  return (
    <>{getComponentByKey(filterKey, name, control, setValue, watch, t(`filter.label.${filterKey}`), values, displayInSearchBar, availableValues)}</>
  );
};
export default FilterComponent;
